/**
 * Theme registry. Each theme is a complete design — not just a colour swap.
 * Themes change the CSS variable palette, the typeface, the radius scale, and
 * the elevation model. Switching themes is `document.documentElement.dataset.theme = id`.
 *
 * The default theme is what the rest of the app is tuned for; the variants
 * are previews. New themes can be added here without touching components.
 */

export interface ThemeTokens {
  /** Background of the page (root). */
  bg: string
  /** Elevated surfaces (cards, sidebars, drawers). */
  bgElevated: string
  /** Subtle elevated surfaces (popovers, menus). */
  bgSubtle: string
  /** Primary text. */
  fg: string
  /** Muted / secondary text. */
  fgMuted: string
  /** Borders / dividers. */
  border: string
  /** Primary accent (buttons, links, active items). */
  accent: string
  /** Text/icon color on top of accent. */
  accentFg: string
  /** Hover state for accent. */
  accentHover: string
  /** Subtle accent (used for tinted backgrounds). */
  accentTint: string
  /** Success colour. */
  success: string
  /** Warning colour. */
  warning: string
  /** Danger colour. */
  danger: string
  /** Border radius scale. */
  radiusSm: string
  radiusMd: string
  radiusLg: string
  /** Font stack override (optional). */
  fontFamily?: string
  /** Mono font stack override (optional). */
  fontMono?: string
}

export interface ThemeDefinition {
  id: string
  name: string
  /** Tags for grouping in the picker. */
  family: 'light' | 'dark' | 'pulp' | 'neon' | 'mono'
  /** Marketing description shown in picker. */
  description: string
  tokens: ThemeTokens
}

export const THEMES: ThemeDefinition[] = [
  {
    id: 'friday-light',
    name: 'Friday Light',
    family: 'light',
    description: 'Default workspace. Clean, bright, easy on the eyes in daylight.',
    tokens: {
      bg: '250 250 251',
      bgElevated: '255 255 255',
      bgSubtle: '244 244 246',
      fg: '26 26 33',
      fgMuted: '110 110 122',
      border: '229 229 234',
      accent: '91 91 214',
      accentFg: '255 255 255',
      accentHover: '73 73 171',
      accentTint: '238 238 254',
      success: '16 185 129',
      warning: '245 158 11',
      danger: '239 68 68',
      radiusSm: '4px',
      radiusMd: '6px',
      radiusLg: '10px',
    },
  },
  {
    id: 'midnight',
    name: 'Midnight',
    family: 'dark',
    description: 'Default dark mode. Slate background, sharp text, signature purple.',
    tokens: {
      bg: '15 15 20',
      bgElevated: '26 26 33',
      bgSubtle: '36 36 44',
      fg: '250 250 251',
      fgMuted: '160 160 171',
      border: '42 42 51',
      accent: '115 118 247',
      accentFg: '15 15 20',
      accentHover: '150 152 250',
      accentTint: '36 36 75',
      success: '34 197 94',
      warning: '251 191 36',
      danger: '248 113 113',
      radiusSm: '4px',
      radiusMd: '6px',
      radiusLg: '10px',
    },
  },
  {
    id: 'pulp',
    name: 'Pulp Friday',
    family: 'pulp',
    description: 'Inspired by the movie. Yellow-green, black outline, hand-drawn vibes.',
    tokens: {
      bg: '245 215 71',
      bgElevated: '252 224 99',
      bgSubtle: '237 200 56',
      fg: '18 18 26',
      fgMuted: '74 74 85',
      border: '18 18 26',
      accent: '18 18 26',
      accentFg: '245 215 71',
      accentHover: '40 40 60',
      accentTint: '231 196 38',
      success: '22 101 52',
      warning: '180 83 9',
      danger: '220 38 38',
      radiusSm: '8px',
      radiusMd: '14px',
      radiusLg: '22px',
      fontFamily: "'Fredoka', 'Inter', system-ui, sans-serif",
    },
  },
  {
    id: 'neon',
    name: 'Cyberpunk',
    family: 'neon',
    description: 'High-contrast neon. For late-night pair-programming with the lights off.',
    tokens: {
      bg: '8 8 28',
      bgElevated: '14 14 44',
      bgSubtle: '22 22 60',
      fg: '230 240 255',
      fgMuted: '140 160 200',
      border: '58 58 110',
      accent: '255 56 192',
      accentFg: '8 8 28',
      accentHover: '255 110 210',
      accentTint: '52 16 70',
      success: '60 255 192',
      warning: '255 196 60',
      danger: '255 80 96',
      radiusSm: '2px',
      radiusMd: '4px',
      radiusLg: '8px',
      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
    },
  },
  {
    id: 'mono',
    name: 'Monochrome',
    family: 'mono',
    description: 'Pure greyscale. Distraction-free. Great for focused deep work.',
    tokens: {
      bg: '255 255 255',
      bgElevated: '250 250 250',
      bgSubtle: '240 240 240',
      fg: '24 24 24',
      fgMuted: '110 110 110',
      border: '215 215 215',
      accent: '24 24 24',
      accentFg: '255 255 255',
      accentHover: '64 64 64',
      accentTint: '235 235 235',
      success: '24 24 24',
      warning: '24 24 24',
      danger: '24 24 24',
      radiusSm: '0',
      radiusMd: '0',
      radiusLg: '0',
    },
  },
]

export const DEFAULT_THEME_ID = 'pulp'

export function findTheme(id: string | null | undefined): ThemeDefinition {
  return THEMES.find((t) => t.id === id) ?? THEMES[0]!
}

/**
 * Apply a theme by writing CSS variables to :root. Variables use space-
 * separated RGB triplets so Tailwind's `rgb(var(--accent) / <alpha-value>)`
 * pattern works as-is. Non-color tokens (radii, fonts) are plain strings.
 */
export function applyThemeTokens(theme: ThemeDefinition): void {
  if (typeof document === 'undefined') return
  const t = theme.tokens
  const root = document.documentElement
  root.dataset.theme = theme.id
  root.style.setProperty('--bg', t.bg)
  root.style.setProperty('--bg-elevated', t.bgElevated)
  root.style.setProperty('--bg-subtle', t.bgSubtle)
  root.style.setProperty('--fg', t.fg)
  root.style.setProperty('--fg-muted', t.fgMuted)
  root.style.setProperty('--border', t.border)
  root.style.setProperty('--accent', t.accent)
  root.style.setProperty('--accent-fg', t.accentFg)
  root.style.setProperty('--accent-hover', t.accentHover)
  root.style.setProperty('--accent-tint', t.accentTint)
  root.style.setProperty('--success', t.success)
  root.style.setProperty('--warning', t.warning)
  root.style.setProperty('--danger', t.danger)
  root.style.setProperty('--radius-sm', t.radiusSm)
  root.style.setProperty('--radius-md', t.radiusMd)
  root.style.setProperty('--radius-lg', t.radiusLg)
  root.style.setProperty('--brand-stroke', t.accent)
  root.style.setProperty('--brand-fill', t.accentFg)
  if (t.fontFamily) {
    root.style.setProperty('--font-sans', t.fontFamily)
  } else {
    root.style.removeProperty('--font-sans')
  }
  if (t.fontMono) {
    root.style.setProperty('--font-mono', t.fontMono)
  } else {
    root.style.removeProperty('--font-mono')
  }
}